import { z } from 'zod';

// Corporate email domains to block
const personalEmailDomains = [
  'gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com', 
  'live.com', 'bol.com.br', 'uol.com.br', 'terra.com.br'
];

// Corporate contact form validation schema
export const contactFormSchema = z.object({
  nome: z.string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres')
    .transform(val => val.trim()),
  
  email: z.string()
    .email('Email inválido')
    .max(255, 'Email deve ter no máximo 255 caracteres')
    .refine(email => {
      const domain = email.split('@')[1]?.toLowerCase();
      return !personalEmailDomains.includes(domain);
    }, 'Por favor, utilize um email corporativo')
    .transform(val => val.toLowerCase().trim()),
  
  telefone: z.string()
    .min(10, 'Telefone deve ter pelo menos 10 dígitos')
    .max(20, 'Telefone deve ter no máximo 20 caracteres')
    .regex(/^(?:\+55\s?)?\(?([0-9]{2})\)?\s?([0-9]{4,5})-?([0-9]{4})$/, 'Formato inválido. Use: (11) 99999-9999')
    .transform(val => val.replace(/\D/g, '')),
  
  empresa: z.string()
    .min(2, 'Nome da empresa é obrigatório')
    .max(100, 'Nome da empresa deve ter no máximo 100 caracteres')
    .transform(val => val.trim()),
  
  cargo: z.string()
    .max(100, 'Cargo deve ter no máximo 100 caracteres')
    .optional()
    .transform(val => val?.trim()),
  
  funcionarios: z.enum(['1-10', '11-50', '51-200', '201-1000', '1000+'])
    .optional(),
  
  interesse: z.enum([
    'fluig-flows',
    'desenvolvimento', 
    'outsourcing',
    'integracao-totvs',
    'consultoria'
  ], {
    required_error: 'Selecione uma área de interesse'
  }),
  
  mensagem: z.string()
    .max(1000, 'Mensagem deve ter no máximo 1000 caracteres')
    .optional()
    .transform(val => val?.trim()),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// Newsletter subscription schema
export const newsletterSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres').optional(),
});

export type NewsletterData = z.infer<typeof newsletterSchema>;

// Fluig project requirements schema
export const fluigProjectSchema = z.object({
  projectType: z.enum([
    'novo-projeto',
    'migracao',
    'customizacao',
    'integracao',
    'suporte'
  ]),
  
  currentVersion: z.string().optional(),
  targetVersion: z.string().optional(),
  
  modules: z.array(z.enum([
    'ecm',
    'bpm',
    'social',
    'identity',
    'analytics',
    'mobile'
  ])),
  
  integrations: z.array(z.string()).optional(),
  
  usersCount: z.enum([
    'ate-50',
    '50-200',
    '200-500',
    '500-1000',
    'acima-1000'
  ]),
  
  complexity: z.enum(['baixa', 'media', 'alta', 'muito-alta']),
  
  hasLegacySystem: z.boolean().default(false),
  legacySystemDetails: z.string().optional(),
});

export type FluigProjectData = z.infer<typeof fluigProjectSchema>;

// Validation helper functions
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[\d\s\-()]+]{10,15}$/;
  return phoneRegex.test(phone);
};

export const formatPhone = (phone: string): string => {
  // Remove all non-numeric characters
  const numbers = phone.replace(/\D/g, '');
  
  // Format Brazilian phone number
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
};

export const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>"']/g, '');
};